import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCog, UserPlus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * SubAdminsCard — broker manages sub-admin assistants for their agency.
 * Each sub-admin has role='agency_admin' + a `sub_admin_permission` level:
 *   • read_only    → View dashboard only, no actions.
 *   • edit         → Can edit client details but not cancel/deactivate.
 *   • edit_cancel  → Can edit + deactivate clients.
 *   • full_admin   → Full agency admin, equivalent to the broker.
 */
const PERMISSION_LABELS = {
    read_only:   "Read only",
    edit:        "Edit",
    edit_cancel: "Edit & cancel",
    full_admin:  "Full admin",
};
const PERMISSION_ORDER = ["read_only", "edit", "edit_cancel", "full_admin"];

export default function SubAdminsCard() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const r = await api.get("/agency/sub-admins");
            setList(r.data.sub_admins || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load sub-admins");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Remove this sub-admin?")) return;
        try {
            await api.delete(`/agency/sub-admins/${id}`);
            toast.success("Removed");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const updatePermission = async (id, permission) => {
        try {
            await api.patch(`/agency/sub-admins/${id}`, { permission });
            toast.success(`Permission set to ${PERMISSION_LABELS[permission]}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <div className="border border-border bg-card rounded overflow-hidden" data-testid="sub-admins-card">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <UserCog className="w-4 h-4 text-primary" />
                    <div className="overline">Sub-admins · {list.length}</div>
                </div>
                <button
                    onClick={() => setOpen(true)}
                    data-testid="sub-admin-add-btn"
                    className="inline-flex items-center gap-1 text-xs border border-border rounded px-2 py-1 hover:bg-secondary/40"
                >
                    <UserPlus className="w-3.5 h-3.5" />
                    Invite assistant
                </button>
            </div>
            {loading ? (
                <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
            ) : list.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground" data-testid="sub-admins-empty">
                    No sub-admins yet. Add assistants with granular permissions to help manage your agency.
                </div>
            ) : (
                <ul className="divide-y divide-border text-sm" data-testid="sub-admins-list">
                    {list.map((s) => (
                        <li key={s.id} className="px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
                            <div>
                                <div className="font-semibold">{s.full_name}</div>
                                <div className="text-xs text-muted-foreground">{s.email} · {s.phone}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <select
                                    value={s.sub_admin_permission || "read_only"}
                                    onChange={(e) => updatePermission(s.id, e.target.value)}
                                    data-testid={`sub-admin-permission-${s.id}`}
                                    className="text-xs border border-border rounded px-2 py-1 bg-background"
                                >
                                    {PERMISSION_ORDER.map((p) => (
                                        <option key={p} value={p}>{PERMISSION_LABELS[p]}</option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => del(s.id)}
                                    data-testid={`sub-admin-del-${s.id}`}
                                    className="text-xs text-danger hover:underline inline-flex items-center gap-1"
                                >
                                    <Trash2 className="w-3 h-3" /> Remove
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            {open && <InviteSubAdminModal onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
        </div>
    );
}

function InviteSubAdminModal({ onClose, onDone }) {
    const [form, setForm] = useState({ full_name: "", email: "", phone: "", initial_password: "", permission: "read_only" });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/agency/sub-admins", form);
            toast.success(`Sub-admin invited (${PERMISSION_LABELS[form.permission]})`);
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invite failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
             onClick={onClose} data-testid="sub-admin-modal">
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                  className="w-full max-w-md bg-card border border-border rounded-lg p-5">
                <h3 className="font-bold text-lg mb-3">Invite an assistant</h3>
                <p className="text-xs text-muted-foreground mb-3">
                    Choose the permission level. You can change it any time.
                </p>
                <div className="space-y-3">
                    {["full_name", "email", "phone", "initial_password"].map((k) => (
                        <label key={k} className="block">
                            <span className="block text-xs font-semibold text-muted-foreground mb-1">
                                {k === "initial_password" ? "Initial password (they rotate on first login)" :
                                 k === "full_name"        ? "Full name" :
                                 k === "phone"            ? "Phone (+254…)" :
                                                             "Email"}
                            </span>
                            <input
                                type={k === "initial_password" ? "password" : k === "email" ? "email" : "text"}
                                required
                                value={form[k]}
                                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                                data-testid={`sub-admin-${k}`}
                                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                            />
                        </label>
                    ))}
                    <label className="block">
                        <span className="block text-xs font-semibold text-muted-foreground mb-1">
                            Permission level
                        </span>
                        <select
                            value={form.permission}
                            onChange={(e) => setForm((f) => ({ ...f, permission: e.target.value }))}
                            data-testid="sub-admin-permission-picker"
                            className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                        >
                            {PERMISSION_ORDER.map((p) => (
                                <option key={p} value={p}>{PERMISSION_LABELS[p]}</option>
                            ))}
                        </select>
                        <div className="text-[10px] text-muted-foreground mt-1">
                            {form.permission === "read_only"   && "View only — cannot modify anything."}
                            {form.permission === "edit"        && "Can edit client details but cannot cancel/deactivate."}
                            {form.permission === "edit_cancel" && "Can edit and deactivate clients."}
                            {form.permission === "full_admin"  && "Full admin — same rights as you."}
                        </div>
                    </label>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                    <button type="button" onClick={onClose}
                            className="px-3 py-1.5 text-sm border border-border rounded"
                            data-testid="sub-admin-cancel">Cancel</button>
                    <button type="submit" disabled={busy}
                            className="px-4 py-1.5 text-sm bg-primary text-primary-foreground font-semibold rounded"
                            data-testid="sub-admin-submit">
                        {busy ? "Inviting…" : "Invite"}
                    </button>
                </div>
            </form>
        </div>
    );
}
